// ** React Imports
import { useState, useEffect, useMemo } from "react";

export const useWebsocket = ({
	conversationId,
	conversation,
	enableDebug,
	loggedUser,
	setVideoChat,
	setConversation,
	setWebsocketInfo,
	sessionId, // To identify one user on several devices because he has the same ID
	closeVideoChat,
}) => {
	const [websocket, setWebsocket] = useState(null);
	const [pingMessagesCount, setPingMessagesCount] = useState(0);
	const [disconnectCount, setDisonnectCount] = useState(0);
	const [firstPingSent, setFirstPingSent] = useState(false);

	const timestamp = useMemo(() => new Date().getTime(), []);
	const socketUrl = useMemo(() => {
		if (conversationId) {
			return `${
				conversation.websocketUrl?.includes("mq2cd")
					? process.env.RAZZLE_CDN_MQ2_URL_WEBSOCKET
					: process.env.RAZZLE_CDN_MQ_URL_WEBSOCKET
			}/chat?_=${timestamp}&channels=${conversation.user.token}`;
		}
		return null;
	}, [
		conversation.user.token,
		timestamp,
		conversation.websocketUrl,
		conversationId,
	]);

	const wsConnect = () => {
		const ws = new WebSocket(socketUrl);

		ws.onopen = () => {
			if (enableDebug) {
				console.log(
					"Połączenie WebSocket zostało nawiązane -> setWebsocket(ws); "
				);
			}
			setWebsocket(ws);
		};

		ws.onmessage = (event) => {
			const dataParse = JSON.parse(event?.data);
			const msg = dataParse.text;

			if (
				msg.type === "custom-ping" &&
				msg.publisherId === loggedUser.id
			) {
				setPingMessagesCount((prev) => prev + 1);
			}

			if (
				msg.conversationId &&
				msg.publisherId &&
				msg.recipientId &&
				msg.conversationId === conversationId &&
				msg.publisherId === loggedUser.id &&
				msg.sessionId &&
				sessionId &&
				msg.sessionId !== sessionId
			) {
				if (msg?.action === "send_new_message") {
					let fileObject = {};
					const { attachment_id, attachment_file } =
						msg.attachment || {};

					if (attachment_id && attachment_file) {
						const attachmentContainer = Math.ceil(
							attachment_id / 10000
						);
						fileObject = {
							file_small: `/attachments/${attachmentContainer}/${attachment_id}/small_${attachment_file}.jpg`,
							file_big: `/attachments/${attachmentContainer}/${attachment_id}/${attachment_file}.jpg`,
							file_id: attachment_id,
						};
					}

					setConversation((prev) => {
						const messageExists = prev.messages.some(
							(message) => message.id === Number(msg?.message_id)
						);
						if (!messageExists) {
							return {
								...prev,
								messages: [
									...prev.messages,
									{
										id: Number(msg?.message_id),
										message: msg?.message_text_api,
										date: msg?.date,
										read: false,
										send: true,
										...fileObject,
									},
								],
							};
						}
						return prev;
					});
				}

				if (msg?.action === "remove_attachment") {
					setConversation((prev) => {
						return {
							...prev,
							forceRefresh: !prev.forceRefresh,
							messages: prev.messages.map((message) => {
								if (message.id === msg?.messageId) {
									return {
										...message,
										file_small: null,
										file_big: null,
										attachmentRemoving: false,
										attachmentIsRemoved: true,
									};
								} else {
									return message;
								}
							}),
						};
					});
				}

				if (msg?.action === "stream_request") {
					setVideoChat((prev) => {
						return {
							...prev,
							secondDevicePublisherAskingAboutStream: Math.floor(
								Math.random() * 9999 + 1
							),
						};
					});
				}

				if (msg?.action === "stream_response_to_request") {
					console.log(msg);
					setVideoChat((prev) => {
						return {
							...prev,
							secondDevicePublisherPicture: msg.picture,
						};
					});
				}

				if (msg?.action === "stream_created") {
					setConversation((prev) => {
						return {
							...prev,
							messages: [
								...prev.messages,
								{
									id: msg.messageDate,
									message: [""],
									additional_status: 1,
									send: true,
									read: false,
									file_big: null,
									file_hash: null,
									file_id: null,
									file_small: null,
									date: msg.messageDate,
								},
							],
						};
					});
					setVideoChat((prev) => {
						return {
							...prev,
							secondDevicePublisherPicture: msg.picture,
						};
					});
					closeVideoChat();
				}

				if (msg?.action === "stream_deleted") {
					setVideoChat((prev) => {
						return {
							...prev,
							secondDevicePublisherPicture: null,
						};
					});
				}
			}

			if (
				msg.conversationId &&
				msg.publisherId &&
				msg.recipientId &&
				msg.conversationId === conversationId &&
				msg.publisherId === conversation.user.id &&
				msg.recipientId === loggedUser.id
			) {
				if (msg?.action === "profile_status") {
					if (enableDebug) {
						console.log(
							`WEBSOCKET - INCOMING (chatPartner) -> change_profile_status - ${msg.profileStatus}`
						);
					}

					setWebsocketInfo({
						chatPartnerProfileStatus: msg.profileStatus,
						chatPartnerOnlineStatus: msg.onlineStatus,
					});
				}

				if (msg?.action === "send_new_message") {
					let fileObject = {};
					const { attachment_id, attachment_file } =
						msg.attachment || {};

					if (attachment_id && attachment_file) {
						const attachmentContainer = Math.ceil(
							attachment_id / 10000
						);
						fileObject = {
							file_small: `/attachments/${attachmentContainer}/${attachment_id}/small_${attachment_file}.jpg`,
							file_big: `/attachments/${attachmentContainer}/${attachment_id}/${attachment_file}.jpg`,
							file_id: attachment_id,
						};
					}

					setConversation((prev) => {
						const messageExists = prev.messages.some(
							(message) => message.id === Number(msg?.message_id)
						);
						if (!messageExists) {
							return {
								...prev,
								messages: [
									...prev.messages,
									{
										id: Number(msg?.message_id),
										message: msg?.message_text_api,
										date: msg?.date,
										read: false,
										send:
											Number(msg?.publisherId) !==
											conversation.user.id,
										...fileObject,
									},
								],
							};
						}
						return prev;
					});
				}

				if (msg?.action === "remove_attachment") {
					setConversation((prev) => {
						return {
							...prev,
							forceRefresh: !prev.forceRefresh,
							messages: prev.messages.map((message) => {
								if (message.id === msg?.messageId) {
									return {
										...message,
										file_small: null,
										file_big: null,
										attachmentRemoving: false,
										attachmentIsRemoved: true,
									};
								} else {
									return message;
								}
							}),
						};
					});
				}

				if (msg?.action === "read_messages") {
					setConversation((prev) => {
						return {
							...prev,
							forceRefresh: !prev.forceRefresh,
							messages: prev.messages.map((item) => ({
								...item,
								read: true,
							})),
						};
					});
				}

				if (msg?.action === "stream_created") {
					if (enableDebug) {
						console.log(
							"WEBSOCKET - INCOMING (chatPartner) -> stream_created"
						);
					}
					setVideoChat((prev) => {
						return {
							...prev,
							chatPartnerStreamStatus: "stream_on",
							watcherHash: msg?.publisherHash,
							chatPartnerStreamPicture: msg?.picture,
						};
					});
					setConversation((prev) => {
						return {
							...prev,
							messages: [
								...prev.messages,
								{
									id: msg.messageDate,
									message: [""],
									additional_status: 1,
									send: false,
									read: false,
									file_big: null,
									file_hash: null,
									file_id: null,
									file_small: null,
									date: msg.messageDate,
								},
							],
						};
					});
				}

				if (msg?.action === "stream_deleted") {
					if (enableDebug) {
						console.log(
							"WEBSOCKET - INCOMING (chatPartner) -> stream_deleted"
						);
					}
					setVideoChat((prev) => {
						return {
							...prev,
							chatPartnerStreamStatus: "stream_off",
							watcherHash: null,
						};
					});
				}

				if (msg?.action === "stream_request") {
					if (enableDebug) {
						console.log(
							"WEBSOCKET - INCOMING (chatPartner) -> stream_request"
						);
					}
					setVideoChat((prev) => {
						return {
							...prev,
							chatPartnerAskingAboutStream: Math.floor(
								Math.random() * 9999 + 1
							),
						};
					});
					setWebsocketInfo({
						chatPartnerProfileStatus: msg.profileStatus,
						chatPartnerOnlineStatus: msg.onlineStatus,
					});
				}

				if (msg?.action === "stream_response_to_request") {
					if (enableDebug) {
						console.log(
							"WEBSOCKET - INCOMING (chatPartner) -> stream_response_to_request",
							msg?.publisherHash
						);
					}
					setVideoChat((prev) => {
						return {
							...prev,
							chatPartnerStreamStatus: "stream_on",
							watcherHash: msg?.publisherHash,
							chatPartnerStreamPicture: msg?.picture,
						};
					});
				}

				if (msg?.action === "stream_audio_and_video_on") {
					if (enableDebug) {
						console.log(
							"WEBSOCKET - INCOMING (chatPartner) -> stream_audio_and_video_on"
						);
					}
					setVideoChat((prev) => {
						return {
							...prev,
							chatPartnerCameraAndMic: "on",
						};
					});
				}

				if (msg?.action === "stream_audio_and_video_off") {
					if (enableDebug) {
						console.log(
							"WEBSOCKET - INCOMING (chatPartner) -> stream_audio_and_video_off"
						);
					}
					setVideoChat((prev) => {
						return {
							...prev,
							chatPartnerCameraAndMic: "off",
						};
					});
				}
			}
		};

		ws.onerror = (error) => {
			if (enableDebug) {
				console.log("Błąd WebSocket:", error);
			}
		};

		ws.onclose = () => {
			if (enableDebug) {
				console.log("Połączenie WebSocket zostało zamknięte.");
			}
		};
	};

	useEffect(() => {
		if (conversation.user.token) {
			wsConnect();
		}
	}, [conversation.user.token]);

	useEffect(() => {
		return () => {
			if (websocket) {
				websocket.close();
			}
		};
	}, [websocket]);

	useEffect(() => {
		if (websocket?.readyState === 1) {
			setPingMessagesCount(0);
			if (conversationId) {
				// Obsługa dociągania po przerwaniu połączenia? ???
			}
		}
	}, [websocket?.readyState]);

	useEffect(() => {
		if (disconnectCount >= 4) {
			return;
		}

		if (websocket?.readyState === 1) {
			const heartbeatTimeoutTime =
				pingMessagesCount === 0 ? 8 * 1000 : 90 * 1000;

			const heartbeatTimeout = setTimeout(() => {
				if (enableDebug) {
					console.log(
						`Minęło ${
							heartbeatTimeoutTime / 1000
						} sekund od ostatniej wiadomości! ); `
					);
				}

				websocket.close();
				setTimeout(() => {
					wsConnect();
				}, 1000);

				setFirstPingSent(false);
				setDisonnectCount((prev) => prev + 1);
			}, heartbeatTimeoutTime);

			const heartbeatIntervalTime = firstPingSent ? 60 * 1000 : 2 * 1000;

			const heartbeatInterval = setInterval(() => {
				if (enableDebug) {
					console.log(
						`Ping co ${heartbeatIntervalTime / 1000} sekund`
					);
				}

				websocket.send(
					JSON.stringify({
						type: "custom-ping",
						publisherId: loggedUser.id,
					})
				);

				if (!firstPingSent) {
					setFirstPingSent(true);
				}
			}, heartbeatIntervalTime);

			return () => {
				clearTimeout(heartbeatTimeout);
				clearInterval(heartbeatInterval);
			};
		}
	}, [
		websocket?.readyState,
		pingMessagesCount,
		disconnectCount,
		firstPingSent,
	]);

	const connectionStatus = {
		0: "Connecting",
		1: "Open",
		2: "Closing",
		3: "Closed",
		4: "Uninstantiated", // Custom value for uninstantiated state
	}[websocket?.readyState ?? 4];

	return { connectionStatus, websocket, pingMessagesCount };
};
